import { makeAutoObservable, runInAction } from 'mobx';
import agent from '../api/agent';
import { ProductGuideDetail, ProductGuideSummary } from '../models/ProductGuide';

// MobX state boundary for the product guide admin screens.
export default class ProductGuideStore {
    productGuideRegistry = new Map<number, ProductGuideSummary>();
    selectedProductGuide: ProductGuideDetail | undefined = undefined;
    loadingInitial = false;
    loadingSave = false;

    constructor() {
        makeAutoObservable(this);
    }

    // Exposes guide rows in render-friendly array form.
    get productGuides() {
        return Array.from(this.productGuideRegistry.values());
    }

    // Loads list rows for the current shop.
    loadProductGuides = async () => {
        this.loadingInitial = true;

        try {
            const guides = await agent.ProductGuides.list();
            runInAction(() => {
                this.productGuideRegistry.clear();
                guides.forEach((guide) => this.productGuideRegistry.set(guide.id, guide));
                this.loadingInitial = false;
            });
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loadingInitial = false;
            });
        }
    }

    // Loads the full editable guide tree for one guide.
    loadProductGuide = async (id: number) => {
        this.loadingInitial = true;

        try {
            const guide = await agent.ProductGuides.details(id);
            runInAction(() => {
                this.selectedProductGuide = guide;
                this.loadingInitial = false;
            });
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loadingInitial = false;
            });
        }
    }

    // Creates or updates a guide and keeps the local registry in sync.
    saveProductGuide = async (guide: ProductGuideDetail) => {
        this.loadingSave = true;

        try {
            const saved = guide.id
                ? await agent.ProductGuides.update(guide.id, guide)
                : await agent.ProductGuides.create(guide);

            runInAction(() => {
                this.selectedProductGuide = saved;
                this.productGuideRegistry.set(saved.id ?? 0, {
                    id: saved.id ?? 0,
                    name: saved.name,
                    handle: saved.handle,
                    description: saved.description,
                    guideType: saved.guideType,
                    templateKey: saved.templateKey,
                    vehicleMode: saved.vehicleMode,
                    isActive: saved.isActive,
                    requiresVehicleContext: saved.requiresVehicleContext,
                    bundleBuilderEnabled: saved.bundleBuilderEnabled,
                    discountEnabled: saved.discountEnabled,
                    discountMinimumItems: saved.discountMinimumItems,
                    discountType: saved.discountType,
                    discountPercentage: saved.discountPercentage,
                    discountFixedAmount: saved.discountFixedAmount,
                    questionCount: saved.questions.length,
                    resultGroupCount: saved.resultGroups.length,
                    recommendedPackageCount: saved.recommendedPackages.length,
                    createdAt: '',
                    updatedAt: null,
                });
                this.loadingSave = false;
            });

            return saved;
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loadingSave = false;
            });
            throw error;
        }
    }

    // Deletes a guide and clears local selection when needed.
    deleteProductGuide = async (id: number) => {
        this.loadingSave = true;

        try {
            await agent.ProductGuides.delete(id);
            runInAction(() => {
                this.productGuideRegistry.delete(id);
                if (this.selectedProductGuide?.id === id) {
                    this.selectedProductGuide = undefined;
                }
                this.loadingSave = false;
            });
        } catch (error) {
            console.log(error);
            runInAction(() => {
                this.loadingSave = false;
            });
            throw error;
        }
    }

    // Clears editor state when leaving create/edit routes.
    clearSelectedProductGuide = () => {
        this.selectedProductGuide = undefined;
    }
}
